import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faPlayCircle,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { SupportVideo } from "../../../services/support-service";
import { BASE_API_URL } from "../../../config/api";

const extractYouTubeId = (url: string): string | null => {
  const regex =
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

const buildStreamUrl = (url: string): string => {
  if (!url || extractYouTubeId(url)) return url;
  const sanitized = decodeURIComponent(
    url
      .replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\/[^/]+\/?/, "")
      .replace(/^\/+/, "")
  );
  return `${BASE_API_URL}local-storage/public-stream-video?fileName=${encodeURIComponent(sanitized)}`;
};

const toRawList = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data && typeof payload.data === "object" && Array.isArray(payload.data.data)) {
    return payload.data.data;
  }
  return [];
};

const normalizeVideoType = (video: any): string =>
  String(video?.videoType ?? video?.type ?? "").trim().toUpperCase();

const toVideoList = (payload: any): SupportVideo[] => {
  return toRawList(payload)
    .map((video: any) => ({
      ...video,
      id: Number(video?.id ?? 0),
      title: String(video?.title ?? ""),
      youtubeUrl: String(video?.youtubeUrl ?? video?.youtube_url ?? ""),
      videoType: (normalizeVideoType(video) || "SUPPORT") as SupportVideo["videoType"],
    }))
    .filter((video) => Boolean(video.youtubeUrl));
};

const SuccessCarousel = () => {
  const [videos, setVideos] = useState<SupportVideo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [index, setIndex] = useState<number>(0);
  const [selectedVideo, setSelectedVideo] = useState<SupportVideo | null>(null);

  useEffect(() => {
    let isActive = true;
    const controller = new AbortController();

    const loadTestimonials = async () => {
      try {
        const response = await fetch(
          `${BASE_API_URL}support-videos`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            cache: "no-store",
            signal: controller.signal,
          },
        );
        let list: SupportVideo[] = [];
        if (response.ok) {
          const payload = await response.json();
          const allVideos = toVideoList(payload);
          list = allVideos.filter((video) => {
            const normalizedType = normalizeVideoType(video);
            const isUploadedFileVideo = !extractYouTubeId(video.youtubeUrl);
            return normalizedType === "TESTIMONIAL" || isUploadedFileVideo;
          });
        }

        if (isActive) {
          setVideos(list);
        }
      } catch (error) {
        if (isActive) {
          setVideos([]);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadTestimonials();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    setIndex(0);
  }, [videos.length]);

  useEffect(() => {
    if (!selectedVideo) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelectedVideo(null);
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [selectedVideo]);

  const activeVideo = useMemo(() => {
    if (videos.length === 0) return null;
    return videos[index];
  }, [videos, index]);

  const activeYouTubeId = activeVideo ? extractYouTubeId(activeVideo.youtubeUrl) : null;
  const activeFileVideoUrl = activeVideo && !activeYouTubeId ? buildStreamUrl(activeVideo.youtubeUrl) : null;

  const selectedYouTubeId = selectedVideo ? extractYouTubeId(selectedVideo.youtubeUrl) : null;
  const selectedFileVideoUrl =
    selectedVideo && !selectedYouTubeId ? buildStreamUrl(selectedVideo.youtubeUrl) : null;

  const next = () => {
    if (videos.length <= 1) return;
    setIndex((i) => (i + 1) % videos.length);
  };

  const prev = () => {
    if (videos.length <= 1) return;
    setIndex((i) => (i - 1 + videos.length) % videos.length);
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-black text-title uppercase tracking-tighter">
            Nos <span className="text-primary italic">Reussites</span>
          </h2>
          <p className="text-sm text-text/40 uppercase tracking-widest mt-2">
            Decouvrez les temoignages video de nos etudiants
          </p>
        </div>

        <div className="relative group max-w-[760px] mx-auto">
          <div className="overflow-hidden rounded-[3rem] shadow-2xl bg-gray-50 border border-gray-100 flex items-center justify-center">
            {loading ? (
              <div className="w-full aspect-video bg-slate-100 animate-pulse" />
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeVideo?.id ?? "empty"}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  className="w-full aspect-video flex items-center justify-center bg-black"
                >
                  {activeVideo ? (
                    <button
                      type="button"
                      onClick={() => setSelectedVideo(activeVideo)}
                      className="group/video relative w-full h-full bg-black outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    >
                      {activeYouTubeId ? (
                        <img
                          src={`https://img.youtube.com/vi/${activeYouTubeId}/maxresdefault.jpg`}
                          onError={(event) => {
                            event.currentTarget.src = `https://img.youtube.com/vi/${activeYouTubeId}/hqdefault.jpg`;
                          }}
                          alt={activeVideo.title || "Temoignage"}
                          className="w-full h-full object-cover"
                        />
                      ) : activeFileVideoUrl ? (
                        <video
                          className="w-full h-full object-contain"
                          muted
                          playsInline
                          preload="metadata"
                          src={activeFileVideoUrl}
                        />
                      ) : (
                        <div className="w-full h-full bg-slate-100 flex flex-col items-center justify-center gap-4">
                          <FontAwesomeIcon
                            icon={faPlayCircle}
                            className="text-6xl text-primary/20 group-hover/video:text-primary transition-colors duration-500"
                          />
                        </div>
                      )}

                      <div className="absolute inset-0 bg-black/35 group-hover/video:bg-black/45 transition-colors duration-300" />

                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6">
                        <FontAwesomeIcon
                          icon={faPlayCircle}
                          className="text-5xl md:text-6xl text-white drop-shadow-xl group-hover/video:scale-110 transition-transform duration-300"
                        />
                        <p className="text-[10px] md:text-[11px] font-black text-white uppercase tracking-[0.35em] text-center">
                          Cliquez pour regarder
                        </p>
                        <p className="text-[11px] md:text-[12px] font-bold text-white/90 text-center line-clamp-2 max-w-3xl">
                          {activeVideo.title || "Temoignage video"}
                        </p>
                      </div>
                    </button>
                  ) : (
                    <div className="w-full h-full bg-slate-100 flex flex-col items-center justify-center gap-4 group/video">
                      <FontAwesomeIcon
                        icon={faPlayCircle}
                        className="text-6xl text-primary/20 group-hover/video:text-primary transition-colors duration-500"
                      />
                      <p className="text-[11px] font-black text-text/20 uppercase tracking-[0.4em] text-center px-4">
                        Aucun temoignage disponible
                      </p>
                      <span className="px-4 py-2 bg-white rounded-full text-[9px] font-bold text-text/40 shadow-sm">
                        Ajoutez des videos temoignages depuis le dashboard admin
                      </span>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            )}
          </div>

          {videos.length > 1 && (
            <>
              <div className="absolute top-1/2 -translate-y-1/2 left-2 md:-left-8">
                <button
                  onClick={prev}
                  className="w-9 h-9 md:w-10 md:h-10 rounded-2xl bg-white border border-gray-100 shadow-xl flex items-center justify-center text-title hover:bg-primary hover:text-white transition-all duration-300"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
              </div>
              <div className="absolute top-1/2 -translate-y-1/2 right-2 md:-right-8">
                <button
                  onClick={next}
                  className="w-9 h-9 md:w-10 md:h-10 rounded-2xl bg-white border border-gray-100 shadow-xl flex items-center justify-center text-title hover:bg-primary hover:text-white transition-all duration-300"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </>
          )}

          {videos.length > 1 && (
            <div className="flex justify-center mt-6 gap-2">
              {videos.map((video, i) => (
                <button
                  key={video.id}
                  onClick={() => setIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    index === i ? "w-8 bg-primary shadow-lg shadow-primary/30" : "w-2 bg-slate-200"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        <AnimatePresence>
          {selectedVideo && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[1200] bg-black/75 backdrop-blur-sm p-4 md:p-8 flex items-center justify-center"
              onClick={() => setSelectedVideo(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="w-full max-w-5xl rounded-[2rem] overflow-hidden bg-black border border-white/10 shadow-2xl"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 bg-black/80 border-b border-white/10">
                  <p className="text-[10px] md:text-[12px] font-black text-white uppercase tracking-[0.25em] truncate pr-4">
                    {selectedVideo.title || "Temoignage video"}
                  </p>
                  <button
                    type="button"
                    onClick={() => setSelectedVideo(null)}
                    className="w-10 h-10 rounded-2xl bg-white/10 text-white hover:bg-white/20 transition-colors"
                    aria-label="Fermer"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>

                <div className="w-full aspect-video bg-black">
                  {selectedYouTubeId ? (
                    <iframe
                      title={selectedVideo.title || "Temoignage video"}
                      src={`https://www.youtube.com/embed/${selectedYouTubeId}?autoplay=1&rel=0&modestbranding=1`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full border-0"
                    />
                  ) : selectedFileVideoUrl ? (
                    <video
                      className="w-full h-full"
                      controls
                      autoPlay
                      preload="metadata"
                      src={selectedFileVideoUrl}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/70 text-sm font-bold">
                      Video indisponible
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default SuccessCarousel;
